import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { Person, Relation, ToneKey } from '@/types/content';
import { getPerson, toneChipClass, toneLineClass } from '@/utils/content';

interface RelationBoardProps {
  centerId: string;
  relations: readonly Relation[];
  activeId?: string | null;
  onSelect?: (id: string) => void;
}

interface Spoke {
  person: Person;
  label: string;
  tone: ToneKey;
}

const toneDot: Record<ToneKey, string> = {
  primary: 'bg-primary-500',
  accent: 'bg-accent-500',
  secondary: 'bg-secondary-400',
  foreground: 'bg-foreground-400',
};

export default function RelationBoard({ centerId, relations, activeId = null, onSelect }: RelationBoardProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [hovered, setHovered] = useState<string | null>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const update = () => setSize({ w: el.clientWidth, h: el.clientHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const center = getPerson(centerId);

  const spokes: Spoke[] = relations
    .filter((r) => r.from === centerId || r.to === centerId)
    .map((r) => {
      const otherId = r.from === centerId ? r.to : r.from;
      const person = getPerson(otherId);
      return person ? { person, label: r.label, tone: r.tone } : null;
    })
    .filter((s): s is Spoke => s !== null);

  if (!center) return null;

  const ready = size.w > 0 && size.h > 0;
  const radius = Math.max(92, Math.min(size.w, size.h) * 0.34);
  const cx = size.w / 2;
  const cy = size.h / 2;
  const n = spokes.length || 1;

  return (
    <div className="w-full">
      <div
        ref={ref}
        className="relative w-full h-[380px] sm:h-[440px] md:h-[500px] rounded-lg bg-background-100 border border-background-200 overflow-hidden"
      >
        {ready && (
          <>
            {spokes.map((s, i) => {
              const theta = (Math.PI * 2 * i) / n - Math.PI / 2;
              const deg = (theta * 180) / Math.PI;
              const nx = cx + radius * Math.cos(theta);
              const ny = cy + radius * Math.sin(theta);
              const isActive = hovered === s.person.id || activeId === s.person.id;
              return (
                <div key={s.person.id}>
                  <div
                    className={`absolute ${toneLineClass[s.tone]} transition-opacity duration-200 ${
                      isActive ? 'opacity-100' : 'opacity-40'
                    }`}
                    style={{
                      left: cx,
                      top: cy - 1,
                      width: radius,
                      height: 2,
                      transformOrigin: '0 50%',
                      transform: `rotate(${deg}deg)`,
                    }}
                    aria-hidden="true"
                  >
                    <span
                      className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 px-2 py-0.5 rounded-full border text-[10px] whitespace-nowrap ${toneChipClass[s.tone]}`}
                    >
                      {s.label}
                    </span>
                  </div>

                  <button
                    type="button"
                    onMouseEnter={() => setHovered(s.person.id)}
                    onMouseLeave={() => setHovered(null)}
                    onClick={() => onSelect?.(s.person.id)}
                    className="absolute z-10 cursor-pointer"
                    style={{ left: nx, top: ny, transform: 'translate(-50%, -50%)' }}
                  >
                    <span className="flex flex-col items-center w-[92px]">
                      <img
                        src={s.person.avatar}
                        alt={`${s.person.name} 头像`}
                        title={`${s.person.name} 与 ${center.name} 的关系 - 911吃瓜`}
                        className={`w-12 h-12 rounded-full object-cover object-top border-2 transition-transform ${
                          isActive ? 'border-primary-500 scale-110' : 'border-background-50'
                        }`}
                      />
                      <span className="mt-1.5 px-2 py-0.5 rounded-md bg-background-50 text-[11px] font-semibold text-foreground-950 whitespace-nowrap">
                        {s.person.name}
                      </span>
                      <span className="mt-0.5 text-[10px] text-foreground-500 whitespace-nowrap">{s.person.role}</span>
                    </span>
                  </button>
                </div>
              );
            })}

            <div className="absolute z-20" style={{ left: cx, top: cy, transform: 'translate(-50%, -50%)' }}>
              <span className="flex flex-col items-center">
                <span className="w-16 h-16 md:w-20 md:h-20 rounded-full p-[3px] bg-primary-500">
                  <img
                    src={center.avatar}
                    alt={`${center.name} 头像`}
                    title={`${center.name} 关系中心 - 911吃瓜`}
                    className="w-full h-full rounded-full object-cover object-top"
                  />
                </span>
                <span className="mt-2 px-2.5 py-0.5 rounded-md bg-primary-500 text-background-50 text-xs font-bold whitespace-nowrap">
                  {center.name}
                </span>
                <span className="mt-0.5 text-[10px] text-foreground-500 whitespace-nowrap">关系中心</span>
              </span>
            </div>
          </>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
        <span className="flex items-center gap-1.5">
          <span className={`w-3 h-1 rounded-full ${toneDot.primary}`} /> 直接关系
        </span>
        <span className="flex items-center gap-1.5">
          <span className={`w-3 h-1 rounded-full ${toneDot.accent}`} /> 合作往来
        </span>
        <span className="flex items-center gap-1.5">
          <span className={`w-3 h-1 rounded-full ${toneDot.secondary}`} /> 团队/机构
        </span>
        <span className="flex items-center gap-1.5">
          <span className={`w-3 h-1 rounded-full ${toneDot.foreground}`} /> 同场关联
        </span>
        {onSelect && <span className="text-foreground-400">点击人物可切换为中心视角</span>}
      </div>
    </div>
  );
}